import type { AskChartSeries } from "@/types/marketReport";

export interface PivotedSeriesMeta {
  /** recharts dataKey (index based, so arbitrary series names are safe). */
  key: string;
  /** Display name for the legend / tooltip. */
  name: string;
}

export type PivotedChartRow = {
  name: string;
} & Record<string, string | number | null | undefined>;

export interface PivotedChartData {
  rows: PivotedChartRow[];
  series: PivotedSeriesMeta[];
}

export const seriesUnitKey = (seriesKey: string) => `${seriesKey}_unit`;

/**
 * Pivots chart series into one row per x-axis label with one value key per series
 * (`s0`, `s1`, …) plus the point's unit under `s0_unit`, etc. Labels keep their
 * first-seen order. A label repeated within the same series gets its own row
 * (so single-series charts render exactly one bar/point per data point).
 */
export function pivotAskChartSeries(
  seriesList: readonly AskChartSeries[],
): PivotedChartData {
  const rows: PivotedChartRow[] = [];
  const rowByKey = new Map<string, PivotedChartRow>();

  const series = seriesList.map((s, seriesIndex) => {
    const key = `s${seriesIndex}`;
    const occurrences = new Map<string, number>();
    for (const point of s.data) {
      const occurrence = occurrences.get(point.label) ?? 0;
      occurrences.set(point.label, occurrence + 1);
      const rowKey = JSON.stringify([point.label, occurrence]);
      let row = rowByKey.get(rowKey);
      if (!row) {
        row = { name: point.label };
        rowByKey.set(rowKey, row);
        rows.push(row);
      }
      row[key] = point.value;
      row[seriesUnitKey(key)] = point.unit;
    }
    return { key, name: s.name || `Series ${seriesIndex + 1}` };
  });

  return { rows, series };
}
